import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, Phone, Calculator, CalendarCheck, Mail, MapPin, Facebook } from "lucide-react";
import Logo, { VeteranBadge, USFlag } from "@/components/Logo";
import { PHONE_DISPLAY, PHONE_TEL, EMAIL, FACEBOOK_URL } from "@/data/site";

const NAV = [
  { to: "/services", label: "Services" },
  { to: "/estimate", label: "Instant Estimate" },
  { to: "/schedule", label: "Request Estimate" },
  { to: "/about", label: "About Paul" },
  { to: "/service-areas", label: "Areas" },
  { to: "/reviews", label: "Reviews" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

function TopRibbon() {
  return (
    <div className="bg-brand-navy text-white" data-testid="top-ribbon">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-2 sm:px-6">
        <p className="flex items-center gap-2 text-xs font-bold tracking-[0.18em] sm:text-xs">
          <USFlag className="h-3 w-4 rounded-[2px]" />
          PROUDLY VETERAN-OWNED &amp; OPERATED — U.S. ARMY
        </p>
        <p className="hidden items-center gap-1.5 text-xs font-semibold tracking-wide text-white/80 lg:flex" data-testid="ribbon-service-area">
          <MapPin className="h-3.5 w-3.5 text-brand-orange" />
          Serving Citrus County · Hernando County · The Villages Area
        </p>
        <a
          href={`tel:${PHONE_TEL}`}
          className="hidden items-center gap-2 text-xs font-bold tracking-wide text-brand-orange transition-colors hover:text-white sm:flex"
          data-testid="ribbon-phone-link"
        >
          <Phone className="h-3.5 w-3.5" /> {PHONE_DISPLAY}
        </a>
      </div>
    </div>
  );
}

function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      className={`sticky top-0 z-50 transition-shadow duration-300 ${
        scrolled ? "glass shadow-lg shadow-brand-blue/5" : "bg-brand-offwhite/90"
      }`}
      data-testid="main-nav"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
        <Link to="/" aria-label="Just Call Paul home" data-testid="nav-logo-link">
          <Logo className="h-14" />
        </Link>
        <nav className="hidden items-center gap-1 xl:flex" aria-label="Primary">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `whitespace-nowrap rounded-full px-3 py-2 text-[13px] font-semibold transition-colors duration-200 ${
                  isActive ? "bg-brand-blue/10 text-brand-blue" : "text-brand-charcoal hover:text-brand-orange"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <a
            href={`tel:${PHONE_TEL}`}
            className="hidden items-center gap-2 whitespace-nowrap rounded-full border-2 border-brand-blue px-4 py-2 text-sm font-bold text-brand-blue transition-colors duration-200 hover:bg-brand-blue hover:text-white lg:flex"
            data-testid="nav-call-button"
          >
            <Phone className="h-4 w-4" /> {PHONE_DISPLAY}
          </a>
          <Link
            to="/estimate"
            className="hidden whitespace-nowrap rounded-full bg-brand-orange px-5 py-2.5 text-sm font-bold text-white shadow-md shadow-brand-orange/30 transition-all duration-200 hover:-translate-y-0.5 hover:bg-brand-orangedark sm:block"
            data-testid="nav-estimate-button"
          >
            Instant Estimate
          </Link>
          <button
            className="rounded-lg p-2.5 text-brand-blue xl:hidden"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>
      {open && (
        <nav className="border-t border-brand-border bg-white px-4 pb-6 pt-3 xl:hidden" aria-label="Mobile" data-testid="mobile-menu">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `block rounded-xl px-4 py-3 text-base font-semibold ${
                  isActive ? "bg-brand-blue/10 text-brand-blue" : "text-brand-charcoal"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <a
            href={`tel:${PHONE_TEL}`}
            className="mt-3 flex items-center justify-center gap-2 rounded-full bg-brand-blue py-3 font-bold text-white"
            data-testid="mobile-call-button"
          >
            <Phone className="h-4 w-4" /> Call Paul — {PHONE_DISPLAY}
          </a>
        </nav>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="bg-brand-navy pb-24 text-white md:pb-0" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <Logo className="h-20" />
            <p className="mt-5 text-sm leading-relaxed text-white/70">
              One Call. Paul Does It All! Locally owned home &amp; property services proudly serving Citrus
              County, Hernando County &amp; The Villages Area — from pressure washing to painting, flooring, and
              everything in between.
            </p>
            <div className="mt-5 flex items-center gap-3">
              <VeteranBadge />
              <a
                href={FACEBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Just Call Paul on Facebook"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-blue text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-brand-orange"
                data-testid="footer-facebook-link"
              >
                <Facebook className="h-4 w-4" />
              </a>
            </div>
          </div>
          <div>
            <h3 className="font-heading text-sm font-bold uppercase tracking-[0.2em] text-brand-orange">Explore</h3>
            <ul className="mt-4 space-y-2.5 text-sm text-white/80">
              {[["/", "Home"], ...NAV.map((n) => [n.to, n.label])].map(([to, label]) => (
                <li key={to + label}>
                  <Link to={to} className="transition-colors hover:text-brand-orange" data-testid={`footer-link-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="font-heading text-sm font-bold uppercase tracking-[0.2em] text-brand-orange">Services</h3>
            <ul className="mt-4 space-y-2.5 text-sm text-white/80">
              <li><Link to="/pressure-washing" className="font-semibold text-white transition-colors hover:text-brand-orange">Pressure Washing &amp; Exterior Cleaning</Link></li>
              <li><Link to="/painting-services" className="transition-colors hover:text-brand-orange">Interior &amp; Exterior Painting</Link></li>
              <li><Link to="/flooring-installation" className="transition-colors hover:text-brand-orange">Flooring Installation &amp; Repairs</Link></li>
              <li><Link to="/handyman-services" className="transition-colors hover:text-brand-orange">Handyman Services &amp; Small Repairs</Link></li>
              <li><Link to="/property-maintenance" className="transition-colors hover:text-brand-orange">Property Maintenance</Link></li>
              <li><Link to="/services" className="transition-colors hover:text-brand-orange">Gutter &amp; Trash-Bin Cleaning</Link></li>
            </ul>
          </div>
          <div>
            <h3 className="font-heading text-sm font-bold uppercase tracking-[0.2em] text-brand-orange">Get In Touch</h3>
            <ul className="mt-4 space-y-3 text-sm text-white/80">
              <li>
                <a href={`tel:${PHONE_TEL}`} className="flex items-center gap-2.5 font-bold text-white transition-colors hover:text-brand-orange" data-testid="footer-phone-link">
                  <Phone className="h-4 w-4 text-brand-orange" /> {PHONE_DISPLAY}
                </a>
              </li>
              <li>
                <a href={`mailto:${EMAIL}`} className="flex items-center gap-2.5 transition-colors hover:text-brand-orange" data-testid="footer-email-link">
                  <Mail className="h-4 w-4 text-brand-orange" /> {EMAIL}
                </a>
              </li>
              <li className="flex items-start gap-2.5">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand-orange" />
                <span>Proudly serving Citrus County, Hernando County &amp; The Villages Area — Inverness, Crystal River, Homosassa, Spring Hill, Brooksville, The Villages &amp; more</span>
              </li>
            </ul>
            <div className="mt-6 flex gap-3">
              <Link to="/estimate" className="flex items-center gap-2 rounded-full bg-brand-orange px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-brand-orangedark" data-testid="footer-estimate-button">
                <Calculator className="h-3.5 w-3.5" /> Instant Estimate
              </Link>
              <Link to="/schedule" className="flex items-center gap-2 rounded-full border border-white/30 px-4 py-2 text-xs font-bold text-white transition-colors hover:border-brand-orange hover:text-brand-orange" data-testid="footer-schedule-button">
                <CalendarCheck className="h-3.5 w-3.5" /> Request Estimate
              </Link>
            </div>
          </div>
        </div>
        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 text-xs text-white/50 sm:flex-row">
          <p>© {new Date().getFullYear()} Just Call Paul — Home &amp; Property Services · justcallpaul352.com · Locally owned, U.S. Army veteran-owned</p>
          <p className="flex gap-5">
            <Link to="/privacy" className="transition-colors hover:text-brand-orange" data-testid="footer-privacy-link">Privacy Policy</Link>
            <Link to="/terms" className="transition-colors hover:text-brand-orange" data-testid="footer-terms-link">Terms &amp; Conditions</Link>
            <Link to="/admin/login" className="transition-colors hover:text-brand-orange" data-testid="footer-admin-link">Owner Login</Link>
          </p>
        </div>
      </div>
    </footer>
  );
}

function StickyMobileCTA() {
  return (
    <div className="fixed inset-x-0 bottom-0 z-50 flex gap-2 border-t border-brand-border bg-white/95 p-3 backdrop-blur-lg md:hidden" data-testid="sticky-mobile-cta">
      <a
        href={`tel:${PHONE_TEL}`}
        className="flex flex-1 items-center justify-center gap-2 rounded-full bg-brand-blue py-3 text-sm font-bold text-white"
        data-testid="sticky-call-button"
      >
        <Phone className="h-4 w-4" /> Call Paul
      </a>
      <Link
        to="/estimate"
        className="flex flex-1 items-center justify-center gap-2 rounded-full bg-brand-orange py-3 text-sm font-bold text-white"
        data-testid="sticky-estimate-button"
      >
        <Calculator className="h-4 w-4" /> Instant Estimate
      </Link>
    </div>
  );
}

export default function Layout() {
  return (
    <>
      <ScrollToTop />
      <TopRibbon />
      <Navbar />
      <main>
        <Outlet />
      </main>
      <Footer />
      <StickyMobileCTA />
    </>
  );
}
